import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  DGCoinFilterOptions,
  DGCoinPlan,
  DGCoinPlanResponse,
  DGCoinPlanState,
} from "./types/dgcoin-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: DGCoinPlanState = {
  dgcoinPlans: [],
  dgcoinPlan: null,
  selectedDGCoinPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

const API_URL = process.env.REACT_APP_API_URL;

export const createDGCoinPlan = createAsyncThunk<
  DGCoinPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/create", async (dgcoinPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/dg-coins`, dgcoinPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create DG Coin plan" },
    );
  }
});

export const fetchDGCoinPlans = createAsyncThunk<
  DGCoinPlanResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/dg-coins`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin plans" },
    );
  }
});

export const fetchDGCoinPlanById = createAsyncThunk<
  DGCoinPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/dg-coins/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch DG Coin plan" },
    );
  }
});

export const deleteDGCoinPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/dg-coins/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete DG Coin plan" },
    );
  }
});

export const updateDGCoinPlan = createAsyncThunk<
  DGCoinPlan,
  { id: number; data: Partial<DGCoinPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/dg-coins/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update DG Coin plan" },
    );
  }
});

export const fetchDGCoinFilterOptions = createAsyncThunk<
  DGCoinFilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("dgcoinPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/dg-coins/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

const dgcoinPlanSlice = createSlice({
  name: "dgcoinPlans",
  initialState,
  reducers: {
    setDGCoinFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearDGCoinFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetDGCoinPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedDGCoinPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createDGCoinPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDGCoinPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dgcoinPlan) {
          state.dgcoinPlans.unshift(action.payload.dgcoinPlan);
        }
      })
      .addCase(createDGCoinPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create DG Coin plan";
      })
      .addCase(fetchDGCoinPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDGCoinPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.dgcoinPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDGCoinPlans.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch DG Coin plans";
      })
      .addCase(fetchDGCoinFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })
      .addCase(fetchDGCoinPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDGCoinPlan = null;
      })
      .addCase(fetchDGCoinPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDGCoinPlan = action.payload;
      })
      .addCase(fetchDGCoinPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch DG Coin plan";
      })
      .addCase(deleteDGCoinPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDGCoinPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinPlans = state.dgcoinPlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteDGCoinPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete DG Coin plan";
      })
      .addCase(updateDGCoinPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDGCoinPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dgcoinPlans = state.dgcoinPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );
        if (state.selectedDGCoinPlan?.id === action.payload.id) {
          state.selectedDGCoinPlan = action.payload;
        }
      })
      .addCase(updateDGCoinPlan.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update DG Coin plan";
      });
  },
});

export const { setDGCoinFilters, clearDGCoinFilters, resetDGCoinPlanState } =
  dgcoinPlanSlice.actions;
export default dgcoinPlanSlice.reducer;
